import { useEffect, useState } from "react";
import { useHub } from "../../context/HubContext";
import { getHubReport } from "../../services/reportService";
import PageHeader from "../../components/common/PageHeader";
import ReportSummaryCards from "../../components/ingroove/reports/ReportSummaryCards";
import RevenueChart from "../../components/ingroove/reports/RevenueChart";
import EventReportCard from "../../components/ingroove/reports/EventReportCard";
import { BarChart2, SlidersHorizontal } from "lucide-react";

const statusFilters = [
  { label: "All", value: "" },
  { label: "Upcoming", value: 0 },
  { label: "Live", value: 1 },
  { label: "Past", value: 2 },
];

const Reports = () => {
  const { hub } = useHub();
  const [report, setReport] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (!hub?.id) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const reportRes = await getHubReport(hub.id);
        setReport(reportRes);
        // Use report events directly for chart — already has ticketRevenue + extrasRevenue
        setChartData(reportRes?.events ?? []);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [hub?.id]);

  const filteredEvents = (report?.events ?? []).filter((e) =>
    statusFilter === "" ? true : e.status === statusFilter,
  );

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-24 bg-bg-surface border border-border rounded-2xl"
            />
          ))}
        </div>
        <div className="h-72 bg-bg-surface border border-border rounded-2xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-52 bg-bg-surface border border-border rounded-2xl"
            />
          ))}
        </div>
      </div>
    );

  if (!report)
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <BarChart2 size={32} className="text-border-hover mb-3" />
        <p className="text-sm text-text-muted">No report data available yet.</p>
      </div>
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        subtitle={`${hub?.name} — all time performance`}
      />

      {/* Summary cards */}
      <ReportSummaryCards report={report} />

      {/* Revenue chart */}
      <div className="bg-bg-surface border border-border rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-text-primary mb-5">
          Revenue by Event
        </h3>
        <RevenueChart data={chartData} />
      </div>

      {/* Event breakdown */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text-primary">
            Event Breakdown
          </h3>
          {/* Status filter */}
          <div className="flex gap-1 bg-bg-surface border border-border rounded-xl p-1">
            {statusFilters.map((s) => (
              <button
                key={s.label}
                onClick={() => setStatusFilter(s.value)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                  statusFilter === s.value
                    ? "ingroove-gradient-bg text-white shadow-glow"
                    : "text-text-muted hover:text-text-secondary"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-bg-surface border border-border rounded-2xl">
            <BarChart2 size={28} className="text-border-hover mb-2" />
            <p className="text-xs text-text-muted">No events for this filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEvents.map((event) => (
              <EventReportCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
